import {Component, OnInit, ViewChild} from '@angular/core';
import {Observable, of, Subject} from 'rxjs';
import {TranslateService} from '@ngx-translate/core';
import {
  ChappsListComponent,
  ChappsListConfig,
  ChappsListFilterConfig,
  ChappsListFiltersModalComponent,
  ChappsListFiltersModalSize,
  ChappsListLayoutOptions,
  ChappsListLayoutType
} from '@npm.chapps.com/chapps.common.angular.library';
import {HttpParams} from '@angular/common/http';
import {ActionStatusType, ActionType, SortDirection} from "../../../common/enums/enums";
import {IssueActionSummary} from "../../../common/model/issue-action-summary.model";
import {Localization} from "../../../common/enums/localization";
import {
  CompactBuildingCardComponent
} from "../../../buildings/components/building-summary/compact-building-card.component";
import {ActionsResource} from "../repository/actions-resource.service";
import {ActionsPerBuildingService} from "../repository/actions-per-building.service";
import {ActionsQuery} from "../repository/actions-query";
import {IdAndLabel} from "../../../common/model/id-and-label";
import {today} from "../../../common/dates/today";
import {backdate, Period} from "../../../common/dates/date-manipulation";
import {ActionsPerBuilding} from "../repository/actions-per-building";
import {CompactBuildingCard} from "../../../buildings/components/building-summary/compact-building-card";
import {map} from "rxjs/operators";
import {
  ChappListLoadResponse
} from "@npm.chapps.com/chapps.common.angular.library/lib/components/chapps-list/interfaces/any-chapps-list-load-response";
import {Destroyable} from "../../../../angular/component/destruction/destroyable.interface";
import {SelfDestruct} from "../../../../angular/component/destruction/self-destruct";
import {InterventionTeamService} from "../../../buildings/service/inspection-team.service";
import {noop} from "underscore";
import {asc, desc} from "../../../../helpers/sorting";

type BuildingGUID = string;
const statuses = [
  ActionStatusType.DECLINED,
  ActionStatusType.ACCEPTED,
  ActionStatusType.EXECUTING,
  ActionStatusType.FINISHED
];
const COUNTED_STATUSES = [
  ActionStatusType.ACCEPTED,
  ActionStatusType.EXECUTING,
];

@Component({
  selector: 'app-actions',
  templateUrl: './actions-list.component.html',
  styleUrls: ['./actions-list.component.scss'],
})
export class ActionsListComponent implements OnInit, Destroyable {
  public readonly destroyed$ = new Subject<ActionsListComponent>();
  public  readonly Localization = Localization;
  //
  @ViewChild(ChappsListComponent, {static: true}) list: ChappsListComponent<IssueActionSummary>;
  // // @ViewChild(AssignModalComponent) assignModalComponent: AssignModalComponent;
  //
  // public buildings: BuildingSummary[];
  // public actions: IssueAction[];
  // private reloadSubject: Subject<any> = new Subject<any>();
  private disableInterventionTeamsSubject: Subject<boolean> = new Subject<boolean>();

  constructor(
    private readonly actionsResource: ActionsResource,
    private readonly actionsPerBuilding: ActionsPerBuildingService,
  //   private buildingsService: BuildingsService,
    private translate: TranslateService,
    private interventionTeamsService: InterventionTeamService,
  //   private addressFormatService: AddressFormatService,
  //   private toast: ToastService,
  //   private route: ActivatedRoute
  ) {}
  

  ngOnInit() {
    this.list.init(this.createListConfig());
  }
  
  ngOnDestroy() {
    SelfDestruct(this);
  }
  //
  // private reload() {
  //   this.reloadSubject.next(null);
  // }
  //
  private createListConfig(): ChappsListConfig<IssueActionSummary> {
    const config = new ChappsListConfig();
    config.setListItemComponentClass(CompactBuildingCardComponent);
    config.setLoadMethod(this.loadBuildingsWithActionsInProgress.bind(this));
    config.setLayout(ChappsListLayoutType.LEFT, {width: 400, offsetTop: 56 + 40} as ChappsListLayoutOptions);
    config.setChildRouteConfig('buildingId', 'guid');
    config.addFilters(this.getFiltersConfig());
    config.filterListActiveFiltersLabel = Localization.ActiveFiltersLabel;
  //   config.addPagination(20);
  //   config.setReloadSubject(this.reloadSubject);
    config.setFiltersModalSize(ChappsListFiltersModalSize.Default);
    config.addOrderByDateButtons();
    return config;
  }
  
  private loadBuildingsWithActionsInProgress(httpParams: HttpParams, pageNumber: number, pageSize: number) {
    const selectionPeriod = parseInt(httpParams.get('selectionPeriodInMonths'));
    const interventionTeams = httpParams.getAll('interventionTeams');
    const type = httpParams.get('Type') as ActionType;
    const executedAfter = backdate(today(), selectionPeriod, Period.Month);
    const propertyToSortBy = httpParams.get('buildingOrder') as keyof CompactBuildingCard;
    const sortDirection = httpParams.get('direction') as SortDirection;
    return this.actionsPerBuilding.all(ActionsQuery.with({statuses, interventionTeams, executedAfter, type})).pipe(
        map(this.buildingActionsAsCard),
        map(this.orderBuildingsBy(propertyToSortBy, sortDirection)),
        map(this.makePagedResponse()),
    );
  }

  private makePagedResponse() {
    return buildingCards => ({
      total: buildingCards.length, page: 1, items: buildingCards
    } as ChappListLoadResponse<CompactBuildingCard>);
  }

  private getFiltersConfig(): ChappsListFilterConfig[] {
    return [
      ChappsListFilterConfig.create('selectionPeriodInMonths', Localization.PeriodShownForFinishedJobs, false,
        null, 3, this.executedAfterSourceFunction.bind(this), ChappsListFiltersModalComponent.RADIO, null
      ),
      ChappsListFilterConfig.create('interventionTeams', Localization.InterventionTeam, true,
        this.interventionTeamValueFunction.bind(this), null, this.interventionTeamSourceFunction.bind(this),
        ChappsListFiltersModalComponent.SELECT, '', 12, noop, this.disableInterventionTeamsSubject
      ),
      ChappsListFilterConfig.create('Type', Localization.JobType, false,
        this.jobTypeValueFunction.bind(this), null, this.jobTypeSourceFunction.bind(this),
        ChappsListFiltersModalComponent.SELECT, '', 12, noop, this.disableInterventionTeamsSubject
      ),
      ChappsListFilterConfig.create('buildingOrder', Localization.SortBy, false,
          value => this.translate.get(value === 'notificationCount' ? Localization.NumberOfActiveJobs : Localization.BuildingName),
        'buildingName', this.buildingOrderSourceFunction.bind(this),
        ChappsListFiltersModalComponent.SELECT, '', 12, noop
      ),
      ChappsListFilterConfig.createForSortDirection()
    ];
  }

  private interventionTeamValueFunction(value): Observable<string> {
    return this.interventionTeamsService.getAll().pipe(
      map(({items: teams}) => {
        const team = teams.find(x => x.id === value);
        return (team != null) ? team.label : '';
      })
    );
  }
  private interventionTeamSourceFunction(): Observable<IdAndLabel[]> {
    return this.interventionTeamsService.getAll()
      .pipe(map(({items: teams}) => teams.map(x => new IdAndLabel(x.id, x.label))));
  }

  private jobTypeValueFunction(value): Observable<string> {
    return this.translate.get(`actionType_${value}`);
  }
  
  private jobTypeSourceFunction(): Observable<IdAndLabel[]> {
    const sourceOptions = Object.values(ActionType).map(type => new IdAndLabel(
      type,
      this.translate.instant(`actionType_${type}`)
    ));
    return of(sourceOptions);
  }

  private buildingOrderSourceFunction(): Observable<IdAndLabel[]> {
    return of([
      new IdAndLabel('buildingLabel', this.translate.instant(Localization.BuildingName)),
      new IdAndLabel('notificationCount', this.translate.instant(Localization.NumberOfActiveJobs)),
    ]);
  }

  private executedAfterSourceFunction(): Observable<IdAndLabel[]> {
    const source = [3, 6, 12].map(extension => {
      return new IdAndLabel(extension, `${extension} ` + this.translate.instant(Localization.Months));
    });
    return of(source);
  }

  private getSortDirectionBySortId(sortId: number): string {
    switch (sortId) {
      case 0:
      case 2:
        return 'asc';
      default:
        return 'desc';
    }
  }

  // public onActionsAssigned() {
  //   this.reload();
  // }

  private get sortParamValue(): string {
    return this.list.filters.sortId >= 2 ? 'WorkOrderReference' : 'DueDate';
  }
  private buildingActionsAsCard(buildingActions: ActionsPerBuilding): CompactBuildingCard[] {
    return buildingActions.all
        .map(([building, actions]) => new CompactBuildingCard(
        building, actions.filter(({status}) => COUNTED_STATUSES.includes(status)).length
    ))
  }

  private orderBuildingsBy(orderBy: keyof CompactBuildingCard, sortDirection: SortDirection) {
    return function (buildingCards: CompactBuildingCard[]) {
      const sortingAlgorithm = sortDirection == SortDirection.DESC.toLowerCase() ? desc : asc;
      console.log('sorting', sortDirection, sortingAlgorithm.name)
      return buildingCards.sort((lhs, rhs) => sortingAlgorithm(lhs[orderBy], rhs[orderBy]));
    };
  }
}
